// DrawerHeader — fixed header of InputDrawerV3.
// Layout: client color strip | title + subtitle | status pill |
// period/month selector | close button.

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Close from "@material-ui/icons/Close";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

import useDrawerStyles from "./styles";
import useProgressStyles from "../progress_drawer/styles";
import useThemeColors from "../../../../hooks/useThemeColors";
import { classifyInputState } from "../InputBoxV3";
import { formatFileTypeLabel } from "../../inputs_components/utils/fileTypes";
import { monthStatusColor } from "../../../../services/inputsV3StateRules";

const statusColor = monthStatusColor;

const STATE_LABEL_KEYS = {
  critical: "modeling_inputs_v3.header.state_critical",
  pending_decision: "modeling_inputs_v3.header.state_pending_decision",
  attention: "modeling_inputs_v3.header.state_attention",
  processing: "modeling_inputs_v3.header.state_processing",
  in_progress: "modeling_inputs_v3.header.state_in_progress",
  in_progress_attention:
    "modeling_inputs_v3.header.state_in_progress_attention",
  delivered_ok: "modeling_inputs_v3.header.state_delivered_ok",
  not_sent: "modeling_inputs_v3.header.state_not_sent",
};

const STATE_PILL_CLS = {
  critical: "statusPillErr",
  pending_decision: "statusPillPending",
  attention: "statusPillWarn",
  processing: "statusPillProcessing",
  in_progress: "statusPillProgress",
  // Onda 2026-05-28: ``in_progress_attention`` ganhou pill proprio
  // (laranja claro saturado) para nao confundir com ``in_progress``
  // (verde claro saturado), alinhado ao split diagonal verde/laranja
  // do swatch de legenda.
  in_progress_attention: "statusPillProgressAttention",
  delivered_ok: "statusPillOk",
  not_sent: "statusPillIdle",
};

function readableName(input, lang, t) {
  const fallback = t("modeling_inputs_v3.header.input_fallback");
  if (!input) return fallback;
  if (input.input_name && typeof input.input_name === "object") {
    return (
      input.input_name[lang] || input.input_name.pt || input.meta_name || "—"
    );
  }
  return input.display_name || input.meta_name || fallback;
}

function periodicityLabel(input, t, reuseOpts) {
  // Onda 2026-05-27: depois da desambiguacao ``fixed → singleton/
  // carry_forward``, cada volatility tem uma semantica clara e util de
  // expor ao consultor no header do drawer. Onda 2026-05-28: remocao
  // de ``semi_fixed`` (consolidado em ``monthly`` + flag ``reuse_*``).
  // Agora:
  //
  //   - monthly       : depende das flags ``reuse_allowed``/``reuse_auto``
  //                     definidas pela UI. Se o consultor MARCOU pelo
  //                     menos uma das duas (cobre a opcao "pode repetir
  //                     entre meses"), mostramos "Mensal · pode repetir
  //                     entre meses". Caso contrario, "Mensal · um
  //                     arquivo por mês" — assim a frase reflete o
  //                     estado atual da configuracao em tempo real.
  //   - yearly        : "Anual · vale para o ano fiscal"
  //   - singleton     : "Arquivo único · vale para todos os meses"
  //   - carry_forward : "Vale do mês do upload em diante" (alinhado
  //                     com o marker_carry_forward da legenda)
  //   - desconhecida  : null (nao inventa info)
  //
  // ``reuseOpts`` (``{ loaded, allowed, auto }``) chega do
  // ``InputDrawerV3`` (campo ``drawerDetails.about``). Enquanto a
  // request do ``about`` nao retornou, ``reuseOpts.loaded === false``
  // e adotamos o label SEM o "pode repetir" — evita que o usuario veja
  // o texto antigo (legado do mes anterior cacheado) durante a janela
  // de carregamento, fenomeno reportado em CCU Testes (mai/2026).
  const vol = input?.volatility;
  switch (vol) {
    case "monthly": {
      const reuseActive =
        !!reuseOpts &&
        reuseOpts.loaded &&
        (reuseOpts.allowed || reuseOpts.auto);
      return reuseActive
        ? t("modeling_inputs_v3.header.semifixed_label")
        : t("modeling_inputs_v3.header.monthly_label");
    }
    case "yearly":
      return t("modeling_inputs_v3.header.yearly_label");
    case "singleton":
      return t("modeling_inputs_v3.header.singleton_label");
    case "carry_forward":
      return t("modeling_inputs_v3.header.carry_forward_label");
    default:
      return null;
  }
}

function formatLabel(input, t) {
  const nature = input?.nature;
  if (nature === "connection" || input?.source === "direct_connection")
    return t("modeling_inputs_v3.header.format_connection");
  if (nature === "semifixed" || input?.source === "auto_generated")
    return t("modeling_inputs_v3.header.format_semifixed");
  const types = input?.expected_file_types ?? input?.expected_file_type;
  const label = formatFileTypeLabel(types);
  if (label)
    return `${t("modeling_inputs_v3.header.format_file")} ${label
      .replace(/\./g, "")
      .replace(/\s+\/\s+/g, " + ")
      .toUpperCase()}`;
  return t("modeling_inputs_v3.header.format_file");
}

function responsibleNamesOf(input) {
  const respList = Array.isArray(input?.responsible) ? input.responsible : [];
  return respList.map((r) => r?.name).filter(Boolean);
}

const DrawerHeader = ({
  input,
  period,
  onClose,
  monthlyStatus,
  selectedMonthIndex,
  onSelectMonth,
  // ``statusLoading=true`` sinaliza que o ``input`` prop pode estar
  // referenciando dados do mes anterior (gap de ~200ms entre clique
  // no card da grid e refresh do snapshot do novo mes). Nesse periodo,
  // mostramos o pill em modo "Carregando..." (cinza neutro) ao inves
  // de pintar com a cor stale. Quando o ``drawerDetails`` recebe o
  // runtime do periodo corrente, o parent reverte para ``false`` e o
  // pill assume a cor real. Defesa contra o flicker visual reportado
  // ao reabrir o drawer apos trocar de mes.
  statusLoading = false,
  // Configuracao corrente de reuso (``drawerDetails.about`` no parent).
  // Usado pelo label da volatility ``monthly`` para diferenciar
  // "Mensal · pode repetir entre meses" (alguma flag ligada) de
  // "Mensal · um arquivo por mês" (ambas desligadas). ``loaded=false``
  // significa que o backend ainda nao respondeu — usamos o label
  // "monthly" para nao mostrar texto contraditorio com a UI durante
  // a janela de carregamento.
  reuseOptions = null,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const progressClasses = useProgressStyles();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "pt";

  // Seletor de mês — mesmo padrão do ``ProgressDrawerHeader``:
  // pílula clicável (abre menu de meses) + chevrons de navegação
  // 1-mês-por-vez. Só ativa quando o caller passa ``monthlyStatus``
  // + ``onSelectMonth``; sem isso, caímos no fallback estático
  // (``periodPill`` com a string ``period``) — preserva o contrato
  // anterior quando o drawer é aberto sem contexto de meses.
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const hasMonthSelector =
    Array.isArray(monthlyStatus) &&
    monthlyStatus.length > 0 &&
    typeof onSelectMonth === "function" &&
    Number.isInteger(selectedMonthIndex);
  const selectedMonth = hasMonthSelector
    ? monthlyStatus[selectedMonthIndex]
    : null;
  const handleOpenMonthMenu = (e) => {
    if (!hasMonthSelector) return;
    setMonthMenuAnchor(e.currentTarget);
  };
  const handleCloseMonthMenu = () => setMonthMenuAnchor(null);
  const handlePickMonth = (idx) => {
    handleCloseMonthMenu();
    if (onSelectMonth) onSelectMonth(idx);
  };
  const canPrevMonth = hasMonthSelector && selectedMonthIndex > 0;
  const canNextMonth =
    hasMonthSelector && selectedMonthIndex < monthlyStatus.length - 1;
  const goPrevMonth = () =>
    canPrevMonth && onSelectMonth(selectedMonthIndex - 1);
  const goNextMonth = () =>
    canNextMonth && onSelectMonth(selectedMonthIndex + 1);

  const displayName = readableName(input, lang, t);
  const andConj = t("modeling_inputs_v3.header.conj_and");
  const state =
    statusLoading || !input ? "processing" : classifyInputState(input);
  // When "critical", distinguish parse failure vs cross-link failure:
  // "File rejected" (parse) vs "Critical cross-link" (xlink).
  const xlState = input?.cross_linking?.state;
  const cs = input?.control_state;
  const systemError = cs === "system_error" || xlState === "system_error";
  const parseFailed = cs === "error" || cs === "system_error";
  const xlinkCritical =
    !parseFailed && (xlState === "error" || xlState === "system_error");
  let resolvedLabel =
    t(
      STATE_LABEL_KEYS[state] || "modeling_inputs_v3.header.state_processing",
    ) || "—";
  if (state === "critical") {
    if (systemError) {
      resolvedLabel = t("modeling_inputs_v3.header.state_processing_error");
    } else if (xlinkCritical) {
      resolvedLabel = t("modeling_inputs_v3.header.state_xlink_critical");
    }
  }
  const stateLabel = statusLoading
    ? t("modeling_inputs_v3.header.state_loading")
    : resolvedLabel;
  // Onda 1.3 / D-35: quando o critico vem de ``system_error`` (parser
  // ou pipeline tecnico) trocamos a pill vermelha por uma pill preta
  // canonica. Distingue visualmente "cliente precisa agir" (vermelho)
  // de "Integration precisa agir" (preto), alinhado a
  // ``MONTH_STATUS_LEGEND.system_error`` em ``inputsV3StateRules.js``.
  const pillCls =
    state === "critical" && systemError
      ? classes.statusPillSystemError
      : classes[STATE_PILL_CLS[state]] || classes.statusPillIdle;

  const respNames = responsibleNamesOf(input);

  // Formata a lista de responsaveis com serial comma em PT/ES/EN,
  // truncando em 3 nomes + indicador "+X" quando houver mais para
  // manter o header legivel:
  //   ["Ana"]                         → "Ana"
  //   ["Ana", "Bruno"]                → "Ana e Bruno"
  //   ["Ana", "Bruno", "Caio"]        → "Ana, Bruno e Caio"
  //   ["Ana", "Bruno", "Caio", "Dani"] → "Ana, Bruno, Caio e +1"
  //   (5 ou mais segue o mesmo padrao com +N)
  const MAX_NAMES = 3;
  let respFragment = null;
  if (respNames.length === 1) {
    respFragment = <strong>{respNames[0]}</strong>;
  } else if (respNames.length === 2) {
    respFragment = (
      <>
        <strong>{respNames[0]}</strong>
        {andConj}
        <strong>{respNames[1]}</strong>
      </>
    );
  } else if (respNames.length === 3) {
    respFragment = (
      <>
        <strong>{respNames[0]}</strong>, <strong>{respNames[1]}</strong>
        {andConj}
        <strong>{respNames[2]}</strong>
      </>
    );
  } else if (respNames.length > MAX_NAMES) {
    const visible = respNames.slice(0, MAX_NAMES);
    const hiddenCount = respNames.length - MAX_NAMES;
    const hiddenNames = respNames.slice(MAX_NAMES).join(", ");
    respFragment = (
      <>
        {visible.map((n, i) => (
          <React.Fragment key={n + i}>
            <strong>{n}</strong>
            {i < visible.length - 1 ? ", " : ""}
          </React.Fragment>
        ))}
        {andConj}
        <strong title={hiddenNames}>+{hiddenCount}</strong>
      </>
    );
  }

  const subtitleParts = [
    periodicityLabel(input, t, reuseOptions),
    formatLabel(input, t),
    respFragment ? (
      <span>
        {t("modeling_inputs_v3.header.responsible_abbr")} {respFragment}
      </span>
    ) : null,
  ].filter(Boolean);

  return (
    <div className={classes.drawerHeader}>
      <div className={classes.clientStrip} />
      <div className={classes.headerMain}>
        <h3 className={classes.headerTitle} title={displayName}>
          {displayName}
        </h3>
        {subtitleParts.length > 0 && (
          <div className={classes.drawerSubtitle}>
            {subtitleParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className={classes.sep} />}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      <div className={`${classes.statusPill} ${pillCls}`} title={stateLabel}>
        {stateLabel}
      </div>
      {hasMonthSelector ? (
        <div className={progressClasses.monthSelectorGroup}>
          <button
            type="button"
            className={progressClasses.monthChevron}
            onClick={goPrevMonth}
            disabled={!canPrevMonth}
            aria-label={t("modeling_inputs_v3.header.prev_month")}
            title={t("modeling_inputs_v3.header.prev_month")}
          >
            <ChevronLeft />
          </button>
          <button
            type="button"
            className={progressClasses.monthSelectorBtn}
            onClick={handleOpenMonthMenu}
            title={t("modeling_inputs_v3.header.change_month")}
          >
            {selectedMonth
              ? `${t("months." + selectedMonth.month.toString())} · ${selectedMonth.year}`
              : "—"}
            <ArrowDropDown />
          </button>
          <button
            type="button"
            className={progressClasses.monthChevron}
            onClick={goNextMonth}
            disabled={!canNextMonth}
            aria-label={t("modeling_inputs_v3.header.next_month")}
            title={t("modeling_inputs_v3.header.next_month")}
          >
            <ChevronRight />
          </button>
        </div>
      ) : (
        period && <div className={classes.periodPill}>{period}</div>
      )}
      <button
        type="button"
        className={classes.drawerClose}
        onClick={onClose}
        aria-label={t("modeling_inputs_v3.header.close")}
      >
        <Close />
      </button>
      {hasMonthSelector && (
        <Menu
          anchorEl={monthMenuAnchor}
          open={Boolean(monthMenuAnchor)}
          onClose={handleCloseMonthMenu}
          className={progressClasses.monthSelectorMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          getContentAnchorEl={null}
        >
          {monthlyStatus.map((m, idx) => (
            <MenuItem
              key={`${m.year}-${m.month}`}
              className={progressClasses.monthMenuItem}
              selected={idx === selectedMonthIndex}
              onClick={() => handlePickMonth(idx)}
            >
              <span
                className="month-status-dot"
                style={{ background: statusColor(m.status) }}
              />
              <span>
                {t("months." + m.month.toString())} {m.year}
              </span>
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};

DrawerHeader.propTypes = {
  input: PropTypes.object,
  period: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  monthlyStatus: PropTypes.array,
  selectedMonthIndex: PropTypes.number,
  onSelectMonth: PropTypes.func,
  statusLoading: PropTypes.bool,
  reuseOptions: PropTypes.shape({
    loaded: PropTypes.bool,
    allowed: PropTypes.bool,
    auto: PropTypes.bool,
  }),
};

export default DrawerHeader;
